import { get, isEmpty } from 'lodash-es'
import { EDateType, EWorkModelType } from '@/helpers/enums'

function makeLocation(ctx, { siteCity, siteStateCode, workModelType }) {
  const address = []

  if (siteCity) {
    address.push(siteCity)
  }

  if (siteStateCode) {
    address.push(siteStateCode)
  }

  if (workModelType === EWorkModelType.REMOTE) {
    const translation = ctx.$t('talent.project.search.label.work.preference.remote')
    const value = ctx.$capitalize(translation)
    address.push(value)
  } else if (workModelType === EWorkModelType.HYBRID) {
    const translation = ctx.$t('talent.project.search.label.work.preference.hybrid')
    const value = ctx.$capitalize(translation)
    address.push(value)
  }

  return address.join(', ')
}

function makeWorkPeriod(ctx, { hoursPerWeek }) {
  return hoursPerWeek
    ? ctx.$t('talent.global.project.public.hours', { hours: hoursPerWeek })
    : undefined
}

function makeDuration(ctx, projectData) {
  const {
    assignmentDuration,
    assignmentDurationVisible,
    assignmentPeriodEnd,
    assignmentPeriodEndVisible,
    assignmentPeriodStart,
    assignmentPeriodStartText,
    assignmentPeriodStartVisible
  } = projectData
  const lines = []

  if (assignmentDurationVisible && assignmentDuration !== 0) {
    lines.push(ctx.$humanizedDate(assignmentPeriodStart, assignmentPeriodEnd))
  }

  if (assignmentPeriodStartVisible) {
    const label = ctx.$t('talent.global.project.details.job.starts')
    let value = assignmentPeriodStartText

    if (isEmpty(assignmentPeriodStartText)) {
      let dateValue = assignmentPeriodStart

      if (typeof assignmentPeriodStart === 'string' && assignmentPeriodStart.includes('T')) {
        const [datePart] = assignmentPeriodStart.split('T')
        dateValue = datePart
      }

      value = ctx.$displayDate({
        value: dateValue,
        format: EDateType.LONG
      })
    }

    lines.push(`${label}: ${value}`)
  }

  if (assignmentPeriodEndVisible && assignmentPeriodEnd) {
    const label = ctx.$t('talent.global.project.details.job.ends')
    let dateValue = assignmentPeriodEnd

    if (typeof assignmentPeriodEnd === 'string' && assignmentPeriodEnd.includes('T')) {
      const [datePart] = assignmentPeriodEnd.split('T')
      dateValue = datePart
    }
    const value = ctx.$displayDate({
      value: dateValue,
      format: EDateType.LONG
    })

    lines.push(`${label}: ${value}`)
  }

  if (
    !assignmentPeriodStartVisible &&
    !assignmentPeriodEndVisible &&
    !assignmentDurationVisible &&
    isEmpty(assignmentPeriodStartText)
  ) {
    ctx.$t('talent.global.project.details.upon.application')
  }

  return lines
}

function makeRates(ctx, { rateRange: { min, max, currency, period } = {} }) {
  if (!(min || max)) {
    return ctx.$t('talent.global.project.details.range.unknown')
  }

  const range = `${min} - ${max}`
  const sign = ctx.$currencySign(currency, period)

  return `${range} ${sign}`
}

export function projectDetails(ctx, projectData) {
  const jobFieldSettings = ctx.$useLeConfig(
    projectData.legalEntityName,
    ctx.$ELeConfig.JOB_FIELD_SETTINGS
  )
  const isVisible = (field) => get(jobFieldSettings.talent, field, 'show') === 'show'

  return [
    {
      id: 'location',
      icon: '$vuetify.icons.mdiMapMarker',
      title: ctx.$t('talent.global.project.details.location'),
      content: makeLocation(ctx, projectData),
      show: isVisible('location'),
      size: 7
    },
    {
      id: 'workPeriod',
      icon: '$vuetify.icons.mdiClock',
      title: ctx.$t('talent.global.project.details.job.hours'),
      content: makeWorkPeriod(ctx, projectData),
      show: isVisible('hoursPerWeek'),
      size: 5
    },
    {
      id: 'duration',
      icon: '$vuetify.icons.mdiCalendarRange',
      title: ctx.$t('talent.global.project.details.timeline'),
      content: makeDuration(ctx, projectData),
      show:
        isVisible('assignmentPeriodStart') ||
        isVisible('assignmentPeriodEnd') ||
        isVisible('assignmentDuration'),
      size: 7
    },
    {
      id: 'rateRange',
      icon: '$vuetify.icons.mdiCalculator',
      title: ctx.$t('talent.global.project.details.job.range'),
      content: makeRates(ctx, projectData),
      show: isVisible('payRateRange'),
      size: 5
    }
  ]
}
